import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  IconCalendar,
  IconCertificate,
  IconClock,
  IconCloseCircle,
  IconCoin,
  IconFlexPools,
  IconLocation,
  IconPause,
  IconProfileUsers,
} from 'assets/img';
import { HourlyRates } from 'shared/components/planner/common/hourly-rates';
import { ShiftDescriptionTooltip } from 'redesign';
import {
  ShiftDetailHeadPropertyItemProps,
  ShiftDetailHeadPropertyRowProps,
} from './shift-detail-head-property-item.types';
import { CertificatesDetail } from '../../../planner/common/certificates-detail';

const ShiftDetailHeadPropertyRow = ({ icon: Icon, children }: ShiftDetailHeadPropertyRowProps) => (
  <CenterTextBox>
    <StyledIcon as={Icon} />
    {children}
  </CenterTextBox>
);

export const ShiftDetailHeadPropertyItem = ({
  propertyKey,
  shiftProperties,
  data,
  showTempWorkRate,
  hideLimitedCompanyShiftFields,
}: ShiftDetailHeadPropertyItemProps) => {
  switch (propertyKey) {
    case 'seats':
      return (
        <ShiftDetailHeadPropertyRow icon={IconProfileUsers}>
          <Typography noWrap variant="subtitle2">
            {shiftProperties.seats}
          </Typography>
        </ShiftDetailHeadPropertyRow>
      );
    case 'startDate':
      return (
        <ShiftDetailHeadPropertyRow icon={IconCalendar}>
          <Typography noWrap variant="subtitle2">
            {shiftProperties.startDate}
          </Typography>
        </ShiftDetailHeadPropertyRow>
      );
    case 'cooldownPeriod':
      return (
        <ShiftDetailHeadPropertyRow icon={IconPause}>
          <Typography noWrap variant="subtitle2">
            {shiftProperties.cooldownPeriod}
          </Typography>
        </ShiftDetailHeadPropertyRow>
      );
    case 'duration':
      return (
        <ShiftDetailHeadPropertyRow icon={IconClock}>
          <Typography noWrap variant="subtitle2">
            {shiftProperties.duration}
          </Typography>
        </ShiftDetailHeadPropertyRow>
      );
    case 'break':
      return (
        <ShiftDetailHeadPropertyRow icon={IconPause}>
          <Typography noWrap variant="subtitle2">
            {shiftProperties.break}
          </Typography>
        </ShiftDetailHeadPropertyRow>
      );
    case 'flexPoolNames':
      return (
        <ShiftDetailHeadPropertyRow icon={IconFlexPools}>
          {shiftProperties.flexPoolNames}
        </ShiftDetailHeadPropertyRow>
      );
    case 'hourlyRates':
      return (
        <Box display="flex" flexDirection="row" marginBottom={3}>
          <HourlyRates
            freelanceWage={data.freelanceProperties?.hourlyRateCents ?? undefined}
            tempworkWage={data.tempWorkProperties?.grossHourlyRateCents ?? undefined}
            showTempWorkRate={showTempWorkRate}
          />
        </Box>
      );
    case 'totalRate':
      return (
        <ShiftDetailHeadPropertyRow icon={IconCoin}>
          <Typography noWrap variant="subtitle2">
            {shiftProperties.totalRate}
          </Typography>
        </ShiftDetailHeadPropertyRow>
      );
    case 'certificates':
      return (
        <ShiftDetailHeadPropertyRow icon={IconCertificate}>
          <CertificatesDetail certificates={shiftProperties.certificates} />
        </ShiftDetailHeadPropertyRow>
      );
    case 'cancelationPeriod':
      return (
        <ShiftDetailHeadPropertyRow icon={IconCloseCircle}>
          <Typography noWrap variant="subtitle2">
            {shiftProperties.cancelationPeriod}
          </Typography>
        </ShiftDetailHeadPropertyRow>
      );
    case 'location':
      return (
        <ShiftDetailHeadPropertyRow icon={IconLocation}>
          <Tooltip title={data.location.address}>
            <Typography noWrap variant="subtitle2">
              {shiftProperties.location}
            </Typography>
          </Tooltip>
        </ShiftDetailHeadPropertyRow>
      );
    case 'description':
      return (
        <ShiftDescriptionTooltip
          descriptionShort={shiftProperties.descriptionShort}
          descriptionLong={shiftProperties.descriptionLong}
          descriptionLawRequirements={shiftProperties.descriptionLawRequirements}
          showLawRequirements={!hideLimitedCompanyShiftFields}
        />
      );
    default: {
      const exhaustiveCheck: never = propertyKey;
      return exhaustiveCheck;
    }
  }
};

const StyledIcon = styled('svg')(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const CenterTextBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(3),
}));
